import { useCallback, useEffect, useRef, useState } from 'react';

export interface DragPosition {
  x: number;
  y: number;
}

export interface DragState {
  isDragging: boolean;
  position: DragPosition;
  hasBeenMoved?: boolean;
}

export interface UseDragOptions {
  initialPosition?: DragPosition;
  onDragStart?: (position: DragPosition) => void;
  onDragMove?: (position: DragPosition) => void;
  onDragEnd?: (position: DragPosition) => void;
  disabled?: boolean;
  enableTouch?: boolean;
  enableMouse?: boolean;
  constrainToViewport?: boolean;
  boundsLeft?: number;
  boundsRight?: number;
  minX?: number;
  minY?: number;
  maxX?: number;
  maxY?: number;
}

export const useDrag = (options: UseDragOptions = {}) => {
  const {
    initialPosition = { x: 0, y: 0 },
    onDragStart,
    onDragMove,
    onDragEnd,
    disabled = false,
    enableTouch = true,
    enableMouse = true,
    constrainToViewport = false,
    boundsLeft,
    boundsRight,
    minX,
    minY,
    maxX,
    maxY,
  } = options;

  const [position, setPosition] = useState<DragPosition>(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [hasBeenMoved, setHasBeenMoved] = useState(false);

  const dragStartPos = useRef<DragPosition>({ x: 0, y: 0 });
  const elementStartPos = useRef<DragPosition>({ x: 0, y: 0 });
  const elementRef = useRef<HTMLDivElement>(null);

  const constrainPosition = useCallback(
    (pos: DragPosition): DragPosition => {
      const constrainedPos = { ...pos };

      if (constrainToViewport && elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;

        const effectiveMinX = boundsLeft ?? 0;
        const effectiveMaxX = (boundsRight ?? window.innerWidth) - width;

        constrainedPos.x = Math.max(
          effectiveMinX,
          Math.min(pos.x, effectiveMaxX),
        );
        constrainedPos.y = Math.max(
          0,
          Math.min(pos.y, window.innerHeight - height),
        );
      }

      if (minX !== undefined)
        constrainedPos.x = Math.max(minX, constrainedPos.x);
      if (maxX !== undefined)
        constrainedPos.x = Math.min(maxX, constrainedPos.x);
      if (minY !== undefined)
        constrainedPos.y = Math.max(minY, constrainedPos.y);
      if (maxY !== undefined)
        constrainedPos.y = Math.min(maxY, constrainedPos.y);

      return constrainedPos;
    },
    [constrainToViewport, boundsLeft, boundsRight, minX, maxX, minY, maxY],
  );

  const handleDragStart = useCallback(
    (clientX: number, clientY: number) => {
      if (disabled) return;

      dragStartPos.current = { x: clientX, y: clientY };
      elementStartPos.current = { ...position };
      setIsDragging(true);

      onDragStart?.(position);
    },
    [position, disabled, onDragStart],
  );

  const handleDragMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging || disabled) return;

      const deltaX = clientX - dragStartPos.current.x;
      const deltaY = clientY - dragStartPos.current.y;

      const newPosition = constrainPosition({
        x: elementStartPos.current.x + deltaX,
        y: elementStartPos.current.y + deltaY,
      });

      setPosition(newPosition);
      setHasBeenMoved(true);
      onDragMove?.(newPosition);
    },
    [isDragging, disabled, constrainPosition, onDragMove],
  );

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);
    onDragEnd?.(position);
  }, [isDragging, position, onDragEnd]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!enableMouse || disabled) return;
      e.preventDefault();
      handleDragStart(e.clientX, e.clientY);
    },
    [enableMouse, disabled, handleDragStart],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      if (!enableTouch || disabled) return;
      const touch = e.touches[0];
      handleDragStart(touch.clientX, touch.clientY);
    },
    [enableTouch, disabled, handleDragStart],
  );

  useEffect(() => {
    if (!isDragging || !enableMouse) return;

    const handleMouseMove = (e: MouseEvent) => {
      handleDragMove(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      handleDragEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, enableMouse, handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (!isDragging || !enableTouch) return;

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      handleDragMove(touch.clientX, touch.clientY);
    };

    const handleTouchEnd = () => {
      handleDragEnd();
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, enableTouch, handleDragMove, handleDragEnd]);

  const bind = {
    ref: elementRef,
    onMouseDown: handleMouseDown,
    onTouchStart: handleTouchStart,
  };

  return {
    position,
    isDragging,
    bind,
    setPosition,
    elementRef,
    hasBeenMoved,
  };
};
